import React, { useState } from 'react';
import {
  ArrowLeft,
  Lasso,
  Paintbrush,
  Eraser,
  Move,
  Undo,
  Redo,
  Maximize2,
  X,
  Loader2,
} from 'lucide-react';
import { generateImage } from '../api/storyboardApi';

// --- 页面 10: AI 改图页 (原页面 9) ---
function InpaintingPage({ onNavigate, pageData }) { 
  
  const originalImage = pageData?.originalImage || 'https://placehold.co/800x450/000000/FFF?text=Original+Image';
  const initialPrompt = pageData?.prompt || '';
  const shots = pageData?.shots || [];
  const activeShotId = pageData?.activeShotId;

  const [activeTool, setActiveTool] = useState('lasso');
  const [activeGenCount, setActiveGenCount] = useState(2);
  const [promptText, setPromptText] = useState(initialPrompt);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  const goBackToFrameEditor = (data = null) => {
    onNavigate('frame-editor', data || { shots });
  };

  // 助手组件: AI改图工具按钮
  const ToolButton = ({ icon: Icon, label, toolId }) => (
    <button
      onClick={() => setActiveTool(toolId)}
      className={`flex flex-col items-center justify-center p-3 rounded-lg w-full h-16 transition-colors
        ${activeTool === toolId ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}
      `}
    >
      <Icon className="w-5 h-5 mb-1" />
      <span className="text-xs">{label}</span>
    </button>
  );

  // 调用 API 生成多张图
  const handleGenerate = async () => {
    const prompt = promptText?.trim();
    if (!prompt) {
      setGenerateError('请填写提示词');
      return;
    }
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const count = activeGenCount;
      const newResults = [];
      for (let i = 0; i < count; i++) {
        const result = await generateImage(prompt, { width: 1024, height: 576 });
        const url = result?.imageUrl;
        if (url) newResults.push({ thumb: url, large: url });
      }
      setResults(newResults);
      setHasGenerated(true);
      setSelectedResult(0);
    } catch (err) {
      console.error('生成失败:', err);
      setGenerateError(err.message || '生成失败，请稍后重试');
    } finally {
      setIsGenerating(false);
    }
  };

  // 选择当前图片并返回画面编辑页
  const handleApply = () => {
    if (selectedResult === null || !results[selectedResult] || !activeShotId) {
      goBackToFrameEditor();
      return;
    }
    const selectedImg = results[selectedResult].large || results[selectedResult].thumb;
    const updatedShots = shots.map(s =>
      s.id === activeShotId ? { ...s, img: selectedImg, largeImg: selectedImg } : s
    );
    onNavigate('frame-editor', { shots: updatedShots });
  };


  return (
    <div className="flex w-full h-full bg-[#121212] overflow-hidden">
      
      {/* 左侧面板 */}
      <div className="w-80 flex-shrink-0 bg-[#1e1e1e] p-4 flex flex-col space-y-4 overflow-y-auto">
        
        {/* "生成结果" 区域 */}
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-2">区域选择</h3>
          <div className="grid grid-cols-4 gap-2">
            <ToolButton icon={Lasso} label="智能选择" toolId="lasso" />
            <ToolButton icon={Maximize2} label="框选" toolId="box" />
            <ToolButton icon={Paintbrush} label="涂抹" toolId="brush" />
            <ToolButton icon={Eraser} label="擦除" toolId="eraser" />
          </div>
        </div>

        {/* 选区调整 */}
        <div className="flex space-x-2">
          <button className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
            增加选区
          </button>
          <button className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
            减少选区
          </button>
        </div>

        {/* 提示词 */}
        <div>
          <label className="text-sm font-medium text-white block mb-2">
            提示词
          </label>
          <textarea
            className="w-full h-24 p-2 bg-[#2a2a2e] border border-gray-600 rounded-md text-gray-200 placeholder-gray-500 text-sm"
            placeholder="输入画面描述..."
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            disabled={isGenerating}
          />
          {generateError && <p className="text-xs text-red-400 mt-1">{generateError}</p>}
        </div>

        {/* 生成张数 */}
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-2">生成张数</h3>
          <div className="flex space-x-2">
            {[1, 2, 3, 4].map(num => (
              <button
                key={num}
                onClick={() => setActiveGenCount(num)}
                disabled={isGenerating}
                className={`w-10 h-10 rounded-lg font-medium transition-colors
                  ${activeGenCount === num ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}
                  disabled:opacity-50
                `}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        {/* **修改3: "生成结果" 区域** */}
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-2">生成结果</h3>
          {/* 仅在有结果时显示网格 */}
          {results.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {results.map((result, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedResult(index)}
                  className={`aspect-square bg-gray-700 rounded-lg overflow-hidden
                    ${selectedResult === index ? 'border-2 border-blue-500 ring-2 ring-blue-500/50' : 'border-2 border-transparent hover:border-gray-500'}
                  `}
                >
                  <img src={result.thumb} alt={`Result ${index + 1}`} className="w-full h-full object-cover rounded-md" />
                </button>
              ))}
            </div>
          ) : (
             // (可选) 占位符，如果 'results' 为空
             <div className="aspect-square bg-gray-800 rounded-lg flex items-center justify-center col-span-4 border-2 border-dashed border-gray-600">
              <p className="text-xs text-gray-500">点击 "立即生成"</p>
             </div>
          )}
        </div>
        
        {/* 底部按钮 (条件渲染) */}
        <div className="flex-grow flex flex-col justify-end space-y-2">
           {!hasGenerated ? (
            <>
              <button 
                onClick={() => goBackToFrameEditor()}
                disabled={isGenerating}
                className="w-full py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
              >
                退出
              </button>
              <button 
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isGenerating && <Loader2 className="w-4 h-4 animate-spin" />}
                {isGenerating ? '生成中...' : '立即生成'}
              </button>
            </>
           ) : (
            <>
              <button 
                onClick={handleApply}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                选择并返回
              </button>
              <button 
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isGenerating && <Loader2 className="w-4 h-4 animate-spin" />}
                {isGenerating ? '生成中...' : '再次生成'}
              </button>
            </>
           )}
        </div>

      </div>

      {/* 右侧主编辑区 */}
      <div className="flex-1 flex flex-col bg-[#121212]">
        {/* 顶部工具栏 */}
        <div className="flex-shrink-0 h-16 bg-[#1a1a1a] p-4 flex justify-between items-center border-b border-gray-700">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => goBackToFrameEditor()}
              className="text-gray-400 hover:text-white"
              title="返回画面编辑"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="h-6 w-px bg-gray-600"></div>
            <button className="text-gray-400 hover:text-white" title="撤销"><Undo className="w-5 h-5" /></button>
            <button className="text-gray-400 hover:text-white" title="重做"><Redo className="w-5 h-5" /></button>
          </div>
          
          <div className="flex items-center space-x-2">
             <button 
               className={`p-2 rounded-md ${activeTool === 'lasso' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
               onClick={() => setActiveTool('lasso')}
               title="智能选择"
             >
               <Lasso className="w-5 h-5" />
             </button>
             <button 
               className="p-2 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-md"
               onClick={() => setActiveTool('box')}
               title="框选"
             >
               <Move className="w-5 h-5" />
             </button>
          </div>
          
          <button 
            onClick={() => goBackToFrameEditor()}
            className="text-gray-400 hover:text-white"
            title="关闭"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 图像画布 */}
        <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
           <img 
              // 关键逻辑:
              // 未生成时: 显示 originalImage
              // 生成后: 显示选中的 results[selectedResult].large
              src={hasGenerated && selectedResult !== null ? results[selectedResult]?.large : originalImage} 
              alt="Inpainting preview"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
        </div>
      </div>

    </div>
  );
}

export default InpaintingPage;
